import React, { useState, useRef } from "react";
import Modal from "react-modal";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { useTranslation } from "react-i18next";

import { signUpCall, signUpKeyCall } from "../../calls";
import { signupError } from "../../utils/serverMessages";

import "./../login/login.css";
import "./../login/auth-signal.css";
import "./signup.css";

Modal.setAppElement("#root");

const sitekey = "4c3e00a9-8947-458a-8e06-46df45f1ae1e";

/* Modale Sign up — design "Signal" (claude.ai/design) */
const Signup = (props) => {
  const { t, i18n } = useTranslation();

  const { setIsLoggedIn, setShowModal2, setShowModal, showModal2, setBalance } = props;

  const [signUpError, setSignUpError] = useState([]);
  const [signupData, setSignupData] = useState({
    email: "",
    password: "",
    confirm_password: "",
  });
  const [token_signup, setTokenSignUp] = useState(null);
  const [trySignUp, setTrySignUp] = useState(false);
  const [isPassHidden, setPassHidden] = useState(true);
  const [isConfirmPassHidden, setConfirmPassHidden] = useState(true);
  // "email" (par defaut) ou "key" : inscription sans e-mail, cle de compte seule.
  const [mode, setMode] = useState("email");
  // Cle affichee une seule fois apres creation ; gardee en memoire uniquement.
  const [accountKey, setAccountKey] = useState("");
  const [keySaved, setKeySaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const captchaRefSignUp = useRef(null);

  const handleDataChange = (e) => {
    setSignupData({ ...signupData, [e.target.name]: e.target.value });
  };

  const resetCaptcha = () => {
    setTokenSignUp(null);
    if (captchaRefSignUp.current && captchaRefSignUp.current.resetCaptcha) {
      captchaRefSignUp.current.resetCaptcha();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setTrySignUp(true);
    if (!token_signup) return;

    signUpCall(signupData, token_signup)
      .then((response) => {
        const { data } = response;
        localStorage.setItem("token", data.token);
        localStorage.setItem("id", data.user_id);
        setIsLoggedIn(true);
        setShowModal2(false);
        setSignupData({ email: "", password: "", confirm_password: "" });
        setSignUpError([]);
        setPassHidden(true);
        setConfirmPassHidden(true);
        setTrySignUp(false);
        resetCaptcha();
      })
      .catch((err) => {
        // Messages connus du serveur (anglais) traduits ; texte d'origine si inconnu.
        // Avant, les erreurs autres que 400 (503 captcha, 500, reseau) n'affichaient rien.
        setSignUpError([signupError(err, t)]);
        setTrySignUp(false);
        resetCaptcha();
      });
  };

  // Inscription par cle : le serveur renvoie la cle de compte (account_key).
  const handleKeySubmit = (e) => {
    e.preventDefault();
    setTrySignUp(true);
    if (!token_signup) return;

    signUpKeyCall(token_signup)
      .then((response) => {
        const { data } = response;
        localStorage.setItem("token", data.token);
        localStorage.setItem("id", data.user_id);
        setIsLoggedIn(true);
        if (setBalance) setBalance(0);
        setSignUpError([]);
        setTrySignUp(false);
        setKeySaved(false);
        setCopied(false);
        setAccountKey(data.account_key || "");
        resetCaptcha();
      })
      .catch((err) => {
        // 429 : trop de comptes crees depuis ce reseau.
        setSignUpError([
          err && err.response && err.response.status === 429
            ? t("signal.key-too-many-accounts")
            : signupError(err, t),
        ]);
        setTrySignUp(false);
        resetCaptcha();
      });
  };

  // Change d'onglet : le jeton du captcha de l'ancien mode ne sert plus.
  const switchMode = (next) => {
    if (next === mode) return;
    setMode(next);
    setSignUpError([]);
    setTrySignUp(false);
    resetCaptcha();
  };

  const copyKey = () => {
    const done = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };
    const fallbackCopy = () => {
      const ta = document.createElement("textarea");
      ta.value = accountKey;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch (e) {
        /* rien : le client peut selectionner la cle a la main */
      }
      document.body.removeChild(ta);
      done();
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(accountKey).then(done).catch(fallbackCopy);
    } else {
      fallbackCopy();
    }
  };

  const downloadKey = () => {
    const text = `2FASMS account key: ${accountKey}\nLogin: https://2fasms.com/app (Log in → Account key)\n`;
    const url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "2fasms-account-key.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Retour a l'etat initial : la cle ne reste pas en memoire.
  const resetKeyScreen = () => {
    setAccountKey("");
    setKeySaved(false);
    setCopied(false);
    setMode("email");
    setSignupData({ email: "", password: "", confirm_password: "" });
    setSignUpError([]);
    setTrySignUp(false);
    setPassHidden(true);
    setConfirmPassHidden(true);
    resetCaptcha();
  };

  // Tant que la cle n'est pas declaree enregistree, la modale ne se ferme pas :
  // c'est le seul moyen de retrouver le compte.
  const closeSignup = () => {
    if (accountKey && !keySaved) return;
    if (accountKey) resetKeyScreen();
    setShowModal2(false);
  };

  return (
    <Modal
      isOpen={showModal2}
      onRequestClose={closeSignup}
      className="auth-modal"
      contentLabel={t("overview.sign-up")}
    >
      <div className="auth-card">
        <div className="auth-card__head">
          <h1 className="auth-card__title">{t("signal.auth-create")}</h1>
          {!accountKey && (
            <button
              type="button"
              className="auth-card__close"
              onClick={closeSignup}
              aria-label={t("signal.close")}
            >
              ✕
            </button>
          )}
        </div>

        {/* Deux modes : e-mail classique, ou compte sans e-mail (cle seule) */}
        {!accountKey && (
          <div className="auth-tabs">
            <button
              type="button"
              className={`auth-tab ${mode === "email" ? "auth-tab--on" : ""}`}
              onClick={() => switchMode("email")}
            >
              {t("signal.auth-mode-email")}
            </button>
            <button
              type="button"
              className={`auth-tab ${mode === "key" ? "auth-tab--on" : ""}`}
              onClick={() => switchMode("key")}
            >
              {t("signal.auth-mode-key")}
            </button>
          </div>
        )}

        {accountKey ? (
          <div className="auth-key">
            <h2 className="auth-card__title">{t("signal.key-title")}</h2>
            <div className="auth-key__value">{accountKey}</div>
            <div className="auth-key__row">
              <button type="button" className="auth-card__alt" onClick={copyKey}>
                {copied ? t("signal.key-copied") : t("signal.key-copy")}
              </button>
              <button type="button" className="auth-card__alt" onClick={downloadKey}>
                {t("signal.key-download")}
              </button>
            </div>
            <p className="auth-key__warn">{t("signal.key-warning")}</p>
            <label className="auth-key__check">
              <input
                type="checkbox"
                checked={keySaved}
                onChange={(e) => setKeySaved(e.target.checked)}
              />
              <span>{t("signal.key-saved")}</span>
            </label>
            <button
              type="button"
              className="auth-card__submit"
              disabled={!keySaved}
              onClick={closeSignup}
            >
              {t("signal.key-continue")}
            </button>
          </div>
        ) : (
          <>
        <p className="auth-card__switch">
          {`${t("overview.already-registered")}`}{" "}
          <button
            type="button"
            className="linkbtn"
            onClick={() => {
              setShowModal2(false);
              setShowModal(true);
            }}
          >
            {`${t("overview.log-in")}`}
          </button>
        </p>

        {signUpError.map((item, index) => (
          <p key={index} className="auth-card__error">{item}</p>
        ))}

        {mode === "email" ? (
        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column", gap: 14 }}
        >
          <div className="auth-field">
            <span className="auth-field__label">{t("signal.auth-email")}</span>
            <div className="auth-field__box">
              <input
                required
                value={signupData.email}
                onChange={handleDataChange}
                type="email"
                id="email"
                name="email"
                placeholder="you@example.com"
                autoComplete="email"
              />
            </div>
          </div>

          <div className="auth-field">
            <span className="auth-field__label">{t("signal.auth-password")}</span>
            <div className="auth-field__box">
              <input
                required
                value={signupData.password}
                onChange={handleDataChange}
                type={isPassHidden ? "password" : "text"}
                id="password"
                name="password"
                placeholder="••••••••"
                autoComplete="new-password"
              />
              <button
                type="button"
                className="auth-field__show"
                onClick={() => setPassHidden(!isPassHidden)}
              >
                {isPassHidden ? t("signal.auth-show") : t("signal.auth-hide")}
              </button>
            </div>
          </div>

          <div className="auth-field">
            <span className="auth-field__label">{t("signal.auth-confirm")}</span>
            <div className="auth-field__box">
              <input
                required
                value={signupData.confirm_password}
                onChange={handleDataChange}
                type={isConfirmPassHidden ? "password" : "text"}
                id="confirm_password"
                name="confirm_password"
                placeholder="••••••••"
                autoComplete="new-password"
              />
              <button
                type="button"
                className="auth-field__show"
                onClick={() => setConfirmPassHidden(!isConfirmPassHidden)}
              >
                {isConfirmPassHidden ? t("signal.auth-show") : t("signal.auth-hide")}
              </button>
            </div>
          </div>

          <div className="hcaptcha">
            <HCaptcha
              id="hcaptcha_signup"
              sitekey={sitekey}
              languageOverride={(i18n.language || "en").split("-")[0]}
              onVerify={(token, eKey) => setTokenSignUp(token)}
              ref={captchaRefSignUp}
            />
            {!token_signup && trySignUp ? (
              <small className="hcaptcha_login_error">
                {`${t("overview.captcha-required")}`}
              </small>
            ) : null}
          </div>

          <button type="submit" className="auth-card__submit">
            {`${t("overview.sign-up")}`}
          </button>
        </form>
        ) : (
        <form
          onSubmit={handleKeySubmit}
          style={{ display: "flex", flexDirection: "column", gap: 14 }}
        >
          <p className="auth-key__intro">{t("signal.key-intro")}</p>

          <div className="hcaptcha">
            <HCaptcha
              id="hcaptcha_signup_key"
              sitekey={sitekey}
              languageOverride={(i18n.language || "en").split("-")[0]}
              onVerify={(token, eKey) => setTokenSignUp(token)}
              ref={captchaRefSignUp}
            />
            {!token_signup && trySignUp ? (
              <small className="hcaptcha_login_error">
                {`${t("overview.captcha-required")}`}
              </small>
            ) : null}
          </div>

          <button type="submit" className="auth-card__submit">
            {t("signal.key-create")}
          </button>
        </form>
        )}

        <div className="auth-card__divider"><span>{t("signal.auth-or")}</span></div>

        <a
          className="auth-card__alt"
          href="https://telegram.me/twofasms"
          target="_blank"
          rel="noreferrer"
        >
          <span className="auth-card__alt-ic">TG</span>
          {t("signal.auth-tg")}
        </a>
          </>
        )}
      </div>
    </Modal>
  );
};

export default Signup;
